/* import __COLOCATED_TEMPLATE__ from './prism-modal.hbs'; */
import Component from '@ember/component';
import { assert, deprecate } from '@ember/debug';
import { inject as service } from '@ember/service';
import { defaultScrollable } from '../utils/scrolling';
const KEYCODE_ESC = 27;
const REPAINT_HACK_SELECTOR = 'body';
const NO_SCROLL_CLASS = 'prism-modal--no-scroll';
export const SCROLLABLE_SELECTOR = '.prism-modal__content, .new--prism-modal-container';
export const MARKET_MODAL_PARTIAL_SELECTOR = 'market-modal-partial';

/**
 * Implements a modal which an arbitrary component can be rendered
 * into. The modal will be rendered into `{{prism-modal-container}}`.
 *
 * ```hbs
 * {{prism-modal-container}}
 *
 * {{#if showModal}}
 *   {{prism-modal
 *     close=(action (mut showModal) false)
 *     bodyComponent=(component "some-component")}}
 * {{/if}}
 * ```
 *
 * @class PrismModal
 */
export default Component.extend({
  tagName: '',
  userAgent: service(),
  /**
   * The component to render in the body of the modal.
   *
   * @argument bodyComponent
   * @type {Component}
   */
  bodyComponent: null,
  /**
   * A function that will be called when closing events occur (i.e. click
   * outside modal, cancels, ESC keys).
   *
   * @argument close
   * @type {Action}
   */
  close: undefined,
  /**
   * If true render an X SVG at the top of the modal for closing.
   *
   * @argument shouldShowCloseX
   * @type {Boolean}
   */
  shouldShowCloseX: false,
  /**
   * True if the modal should use small Prism styling, vs default larger
   * modal styling.
   *
   * @argument isNarrow
   * @type {Boolean}
   */
  isNarrow: false,
  /**
   * True if the modal uses the bottom sheet style on small screens.
   *
   * @argument enableBottomSheet
   * @type {Boolean}
   */
  enableBottomSheet: false,
  /**
   * Render this modal using the V2 styles and markup.
   *
   * @argument useNewModal
   * @type {Boolean}
   */
  useNewModal: false,
  /**
   * Add a class to the modal's content to enable custom styling.
   *
   * @argument contentClass
   * @type {String}
   */
  contentClass: '',
  init(...args) {
    this._super(...args);
    assert('Must provide a close action', this.close);
    deprecate('PrismModal component is deprecated. Use Prism::Modal instead (see http://go/emberprismdocs/docs/styleguide/modals-new)', {
      id: 'ember-prism.old-modals-removal',
      until: '2.0.0',
      for: '@square/ember-prism',
      since: '1.5.0'
    });

    // The bottom sheet only works with new modals.
    if (this.enableBottomSheet) {
      this.set('useNewModal', true);
    }
  },
  didInsertElement() {
    const element = defaultScrollable();

    // If the modal is visible, we want to apply no-scroll to
    // the body of the page, so that the user scrolls in the modal
    // but not the page.
    this.onScrollableKeyUp = event => {
      // Close modal if escape key is pressed
      if (event.keyCode === KEYCODE_ESC) {
        this.close();
      }
    };
    element.addEventListener('keyup', this.onScrollableKeyUp);
    if (!this.useNewModal) {
      element.classList.add(NO_SCROLL_CLASS);
    }
    this._forceRepaint();
  },
  willDestroy() {
    const element = defaultScrollable();
    element.removeEventListener('keyup', this.onScrollableKeyUp);
    if (!this.useNewModal) {
      element.classList.remove(NO_SCROLL_CLASS);
    }
    this._forceRepaint();
  },
  // This selector can be overridden by an app by re-exporting this component
  // in it's namespace.
  _repaintHackSelector: REPAINT_HACK_SELECTOR,
  // On iOS Safari we have observed rendering issues when opening and closing
  // modals. The DOM will be in the correct state but the rendered page will be
  // broken. Forcing the browser to repaint the page appears to fix the issue.
  //
  // https://jira.sqcorp.co/browse/CBF-981
  // https://stackoverflow.com/questions/8840580/force-dom-redraw-refresh-on-chrome-mac
  _forceRepaint() {
    if (this.userAgent.os.isIOS) {
      const hackElement = document.querySelector(this._repaintHackSelector);
      hackElement.style.display = 'none';
      hackElement.style.display = '';
    }
  }
});